import { useCallback, useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import { ajouterClasse, enfantsParClasse, modifierClasse } from '../../../lib/jpa/gateway.supabase';
import { classesDuCentre, journeesParClasse, supprimerClasse } from '../../../lib/centres/gateway.supabase';
import { consequencesDeLaSuppression, messageDeLaBase, ordreApresDeplacement } from '../../../lib/centres/regles';
import type { ClasseDuCentre } from '../../../lib/jpa/regles';

/*
  Les classes d'un centre, au nom de l'instituteur : c'est ce que les parents
  reconnaissent quand ils inscrivent leur enfant à une journée pédagogique.

  Elles s'ajoutent, se renomment, se rangent, se masquent aux parents (la
  classe garde ses élèves, l'année passée par exemple) ou se suppriment. La
  suppression dit avant le clic ce qu'elle fait : ses élèves passent dans
  une autre classe du centre, ou n'en ont plus et leurs parents la
  choisiront ; aucune inscription n'est touchée (`supprimer_classe`).
*/

interface Props {
  centreId: string;
}

export default function ClassesDuCentre({ centreId }: Props) {
  const [classes, setClasses] = useState<ClasseDuCentre[]>([]);
  const [eleves, setEleves] = useState<Map<string, number>>(new Map());
  const [journees, setJournees] = useState<Map<string, number>>(new Map());
  const [nouvelle, setNouvelle] = useState('');
  const [edition, setEdition] = useState<{ id: string; libelle: string } | null>(null);
  const [aSupprimer, setASupprimer] = useState<ClasseDuCentre | null>(null);
  const [remplacante, setRemplacante] = useState('');
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const relire = useCallback(async () => {
    const lues = await classesDuCentre(centreId);
    const ids = lues.map((c) => c.id);
    const [e, j] = await Promise.all([enfantsParClasse(ids), journeesParClasse(ids)]);
    setClasses(lues);
    setEleves(e);
    setJournees(j);
  }, [centreId]);

  useEffect(() => {
    relire()
      .catch((e) => setErreur(messageDeLaBase(e)))
      .finally(() => setChargement(false));
  }, [relire]);

  const agir = async (geste: () => Promise<void>, fait: string) => {
    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      await geste();
      await relire();
      setMessage(fait);
    } catch (e) {
      setErreur(messageDeLaBase(e));
    } finally {
      setEnCours(false);
    }
  };

  const deplacer = (index: number, sens: -1 | 1) => agir(async () => {
    for (const { id, ordre } of ordreApresDeplacement(classes, index, sens)) await modifierClasse(id, { ordre });
  }, 'Ordre enregistré.');

  const ouvrirSuppression = (c: ClasseDuCentre) => {
    setRemplacante('');
    setASupprimer(c);
  };

  const supprimer = async () => {
    if (!aSupprimer) return;
    const c = aSupprimer;
    await agir(async () => {
      await supprimerClasse(c.id, remplacante || null);
      setASupprimer(null);
    }, `Classe « ${c.libelle} » supprimée.`);
  };

  const nomRemplacante = classes.find((c) => c.id === remplacante)?.libelle ?? null;

  return (
    <div className="space-y-4">
      {erreur && <p className="p-3 bg-red-50 text-red-700 rounded-lg" role="alert">{erreur}</p>}
      {message && <p className="p-3 bg-green-50 text-green-800 rounded-lg" role="status">{message}</p>}

      <form
        className="flex flex-wrap gap-2 items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (!nouvelle.trim()) return;
          void agir(async () => {
            await ajouterClasse(centreId, nouvelle, classes.reduce((m, c) => Math.max(m, c.ordre), 0) + 1);
            setNouvelle('');
          }, 'Classe ajoutée.');
        }}
      >
        <label className="flex-1 min-w-[240px]">
          <span className="block text-sm font-semibold text-gray-700 mb-1">Nouvelle classe</span>
          <input value={nouvelle} onChange={(e) => setNouvelle(e.target.value)} placeholder="Ex. 2e primaire A — Madame Claire" className="form-input" />
        </label>
        <button type="submit" disabled={enCours || !nouvelle.trim()} className="btn-primary disabled:opacity-50">Ajouter</button>
      </form>

      {chargement ? (
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
      ) : classes.length === 0 ? (
        <p className="text-sm text-gray-600">Aucune classe pour ce centre.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-gray-700 border-b-2 border-gray-200">
              <tr>
                <th className="px-2 py-2 font-semibold">Classe</th>
                <th className="px-2 py-2 font-semibold text-right">Élèves rangés</th>
                <th className="px-2 py-2 font-semibold text-right">Journées</th>
                <th className="px-2 py-2 font-semibold">Pour les parents</th>
                <th className="px-2 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c, n) => (
                <tr key={c.id} className="border-b border-gray-100">
                  <td className="px-2 py-2">
                    {edition?.id === c.id ? (
                      <input
                        value={edition.libelle}
                        onChange={(e) => setEdition({ id: c.id, libelle: e.target.value })}
                        aria-label="Nom de la classe"
                        className="form-input"
                      />
                    ) : (
                      <span className={c.active ? '' : 'text-gray-500'}>{c.libelle}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">{eleves.get(c.id) ?? 0}</td>
                  <td className="px-2 py-2 text-right">{journees.get(c.id) ?? 0}</td>
                  <td className="px-2 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {c.active ? 'Proposée' : 'Masquée'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {edition?.id === c.id ? (
                      <span className="inline-flex gap-1">
                        <button
                          type="button"
                          disabled={enCours || !edition.libelle.trim()}
                          className="btn-primary text-sm py-1 disabled:opacity-50"
                          onClick={() => void agir(async () => {
                            await modifierClasse(c.id, { libelle: edition.libelle });
                            setEdition(null);
                          }, 'Classe renommée.')}
                        >
                          Enregistrer
                        </button>
                        <button type="button" className="btn-outline text-sm py-1" onClick={() => setEdition(null)}>Annuler</button>
                      </span>
                    ) : (
                      <span className="inline-flex gap-1">
                        <button type="button" aria-label={`Monter ${c.libelle}`} disabled={enCours || n === 0} onClick={() => void deplacer(n, -1)} className="btn-outline text-sm py-1 px-2 disabled:opacity-40">
                          <ArrowUp className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button type="button" aria-label={`Descendre ${c.libelle}`} disabled={enCours || n === classes.length - 1} onClick={() => void deplacer(n, 1)} className="btn-outline text-sm py-1 px-2 disabled:opacity-40">
                          <ArrowDown className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button type="button" className="btn-outline text-sm py-1" onClick={() => setEdition({ id: c.id, libelle: c.libelle })}>Renommer</button>
                        <button
                          type="button"
                          disabled={enCours}
                          className="btn-outline text-sm py-1"
                          onClick={() => void agir(() => modifierClasse(c.id, { active: !c.active }), c.active ? 'Classe masquée aux parents.' : 'Classe proposée aux parents.')}
                        >
                          {c.active ? 'Masquer' : 'Proposer'}
                        </button>
                        <button type="button" disabled={enCours} className="btn-outline text-sm py-1 text-red-700 border-red-300 hover:bg-red-50" onClick={() => ouvrirSuppression(c)}>
                          Supprimer
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-sm text-gray-600">
        « Masquer » retire une classe du choix des parents en gardant ses élèves, l’année passée par exemple. « Supprimer » l’efface.
      </p>

      <Dialog open={aSupprimer !== null} onClose={() => !enCours && setASupprimer(null)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40" />
          {aSupprimer && (
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
              <Dialog.Title className="text-lg font-bold text-gray-900">Supprimer « {aSupprimer.libelle} » ?</Dialog.Title>
              {(eleves.get(aSupprimer.id) ?? 0) > 0 && (
                <label className="block">
                  <span className="block text-sm font-semibold text-gray-700 mb-1">Ses élèves passent dans</span>
                  <select value={remplacante} onChange={(e) => setRemplacante(e.target.value)} className="form-input">
                    <option value="">Aucune classe — les parents la choisiront</option>
                    {classes.filter((c) => c.id !== aSupprimer.id).map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
                  </select>
                </label>
              )}
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                {consequencesDeLaSuppression(eleves.get(aSupprimer.id) ?? 0, journees.get(aSupprimer.id) ?? 0, nomRemplacante)
                  .map((p) => <li key={p}>{p}</li>)}
              </ul>
              <div className="flex justify-end gap-3">
                <button type="button" disabled={enCours} onClick={() => setASupprimer(null)} className="btn-outline">Annuler</button>
                <button type="button" disabled={enCours} onClick={() => void supprimer()} className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50">
                  {enCours ? 'Suppression…' : 'Supprimer la classe'}
                </button>
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
